import { ArrowDropDown } from "@mui/icons-material";
import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useRef, useState } from "react";

const POPPER_Z_INDEX_OFFSET = 1;
const LABEL_WEIGHT = 600;
const TOGGLE_MAX_WIDTH = 40;

interface SplitButtonProps {
  label: string;
  disabled?: boolean;
  onPrimaryClick: () => void;
  onSecondaryClick: () => void;
}

interface PrimaryButtonsProps {
  label: string;
  disabled: boolean;
  anchorRef: React.RefObject<HTMLDivElement | null>;
  onPrimaryClick: () => void;
  onToggle: () => void;
}

const PrimaryButtons = ({ label, disabled, anchorRef, onPrimaryClick, onToggle }: PrimaryButtonsProps): React.ReactElement => (
  <ButtonGroup
    variant="contained"
    ref={anchorRef}
    disabled={disabled}
    fullWidth
    sx={{ mt: 2 }}
  >
    <Button
      onClick={onPrimaryClick}
      sx={{ textTransform: "none", fontWeight: LABEL_WEIGHT, flex: 1 }}
    >
      {label}
    </Button>
    <Button
      size="small"
      onClick={onToggle}
      sx={{ maxWidth: TOGGLE_MAX_WIDTH }}
      aria-label="open options"
    >
      <ArrowDropDown />
    </Button>
  </ButtonGroup>
);

interface DropdownMenuProps {
  open: boolean;
  anchorEl: HTMLDivElement | null;
  zIndex: number;
  onClose: (event: Event) => void;
  onSecondaryClick: () => void;
}

const DropdownMenu = ({ open, anchorEl, zIndex, onClose, onSecondaryClick }: DropdownMenuProps): React.ReactElement => (
  <Popper
    open={open}
    anchorEl={anchorEl}
    placement="bottom-end"
    transition
    sx={{ zIndex }}
  >
    {({ TransitionProps: transitionProps }) => (
      <Grow {...transitionProps}>
        <Paper elevation={4}>
          <ClickAwayListener onClickAway={onClose}>
            <MenuList autoFocusItem>
              <MenuItem onClick={onSecondaryClick}>
                Open in new tab
              </MenuItem>
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Grow>
    )}
  </Popper>
);

const SplitButton = ({ label, disabled = false, onPrimaryClick, onSecondaryClick }: SplitButtonProps): React.ReactElement => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleToggle = (): void => {
    setOpen((prev) => !prev);
  };

  const handleClose = (event: Event): void => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  const handleSecondaryClick = (): void => {
    setOpen(false);
    onSecondaryClick();
  };

  return (
    <>
      <PrimaryButtons
        label={label}
        disabled={disabled}
        anchorRef={anchorRef}
        onPrimaryClick={onPrimaryClick}
        onToggle={handleToggle}
      />
      <DropdownMenu
        open={open}
        anchorEl={anchorRef.current}
        zIndex={theme.zIndex.modal + POPPER_Z_INDEX_OFFSET}
        onClose={handleClose}
        onSecondaryClick={handleSecondaryClick}
      />
    </>
  );
};

export default SplitButton;
